/* eslint-disable */
// @ts-nocheck
import { mdiCalendarOutline, mdiCalendarRemoveOutline } from '@mdi/js';
import { Popover } from '@mui/material';
import { addMinutes, format as dateFnFormat, parse, parseISO, isValid} from 'date-fns';
import _ from 'lodash';
import * as React from 'react';
import { DayPicker, DayPickerProps, NavbarElementProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import { BaseInput } from '../input';
import { DatePickerNavbar } from './datepicker-navbar';
import * as S from './datepicker.styles';

export function toUTC(date: Date): Date {
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error Input props
export interface IDatePickerComponentProps {
    onBlur: () => void;
    onChange: () => void;
    onClick: () => void;
    onFocus: () => void;
    onKeyDown: () => void;
    onKeyUp: () => void;
    placeholder: string;
    readOnly: boolean;
    value: string;
}

export interface IDatePickerProps {
    disabled?: boolean;
    inputProps?: Partial<IDatePickerComponentProps>;
    customDayPickerProps?: Partial<DayPickerProps>;
    onDateSelected?: (date: Date | undefined) => void;
    format?: string;
    value?: string | Date;
    name?: string;
    errorMessage?: string;
}

const defaultProps = {
    format: 'yyyy-MM-dd',
};

function formatTimezone(date: Date, formatString: string) {
    return format(addMinutes(date, date.getTimezoneOffset()), formatString);
}

export const DatePicker: React.FunctionComponent<IDatePickerProps> = React.memo(
    ({ value: propsVal, format, onDateSelected, disabled, inputProps, customDayPickerProps, errorMessage }) => {
        const [anchorElement, setAnchorElement] = React.useState<Element>();
        const [value, setValue] = React.useState(propsVal);
        const [currentMonth, setCurrentMonth] = React.useState<Date>(new Date());

        // Hold the selected date in state
        const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined);

        // Sync internal state when the external value prop changes (e.g. programmatic prefill)
        React.useEffect(() => {
            if (propsVal != null && propsVal !== '') {
                let dateToUse: Date | undefined;
                let stringValue: string;

                if (propsVal instanceof Date) {
                    if (isValid(propsVal)) {
                        dateToUse = propsVal;
                        stringValue = formatTimezone(propsVal, dateFormat());
                    } else {
                        setValue('');
                        setSelectedDate(undefined);
                        setCurrentMonth(new Date());
                        return;
                    }
                } 
                else {
                    stringValue = propsVal;
                    const parsedDate = parse(propsVal, dateFormat(), new Date());
                    if (isValid(parsedDate)) {
                        dateToUse = parsedDate;
                    } else {
                        setValue('');
                        setSelectedDate(undefined);
                        setCurrentMonth(new Date());
                        return;
                    }
                }

                setValue(stringValue);
                setSelectedDate(dateToUse);
                setCurrentMonth(dateToUse);
            } else {
                setValue('');
                setSelectedDate(undefined);
                setCurrentMonth(new Date());
            }
        }, [propsVal]);


        const renderDayPicker = (): React.ReactElement => {
            const dayPickerProps: DayPickerProps = {
                selected: selectedDate,
                month: currentMonth,
                mode: "single",
                onMonthChange: setCurrentMonth,
                onSelect: handleDayPickerSelect,
                showOutsideDays: true,
                navbarElement: renderNavbarElement,
                captionElement: <></>,
                ...customDayPickerProps,
            };
            return <DayPicker {...dayPickerProps} />;
        };

        const renderNavbarElement = (props: NavbarElementProps): React.ReactElement => {
            const month = format(props.month, 'MMMM yyyy');
            return (
                <DatePickerNavbar
                    isShowRange={false}
                    month={month}
                    showPreviousButton={props.showPreviousButton}
                    onPreviousClick={props.onPreviousClick}
                    showNextButton={props.showNextButton}
                    onNextClick={props.onNextClick}
                />
            );
        };

        const dateFormat = (): string => {
            return format || defaultProps.format;
        };

        const formatDate = (date: Date): string => {
            return formatTimezone(date, dateFormat());
        };

        const handleDayClick = (date: Date): void => {
            const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            pickDate(utcDate);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (e && e.nativeEvent) {
                if (e.nativeEvent.key === 'ArrowDown') {
                    showDayPicker(e);
                }
            }
        };

        const showDayPicker = (e: React.MouseEvent | React.KeyboardEvent): void => {
            const element = e.currentTarget as Element;
            setAnchorElement(element);
        };

        const hideDayPicker = (): void => {
            setAnchorElement(undefined);
        };

        const handleDayPickerSelect = (date: Date | undefined) => {
            if (!date) {
                setValue("");
                setSelectedDate(undefined);
                onDateSelected(undefined);
            } else {
                setSelectedDate(date);
                setCurrentMonth(date);
                setValue(dateFnFormat(date, dateFormat()));
                onDateSelected(date);
            }
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value); // Keep the input value in sync

            // Use midnight as reference to ensure consistent time
            const referenceDate = new Date();
            referenceDate.setHours(0, 0, 0, 0);
            const parsedDate = parse(e.target.value, dateFormat(), referenceDate);

            if (isValid(parsedDate)) {
                setSelectedDate(parsedDate);
                setCurrentMonth(parsedDate);
                onDateSelected(parsedDate);
            } else {
                setSelectedDate(undefined);
                onDateSelected(undefined);
            }
        };

        return (
            <S.DatePicker>
                <BaseInput
                    disabled={disabled}
                    {...(inputProps && {
                        onBlur: inputProps.onBlur,
                        onClick: inputProps.onClick,
                        onFocus: inputProps.onFocus,
                        onKeyUp: inputProps.onKeyUp,
                        readOnly: inputProps.readOnly,
                    })}
                    errorMessage={errorMessage}
                    name={name}
                    type="text"
                    onKeyDown={handleKeyDown}
                    placeholder={dateFormat()}
                    value={value}
                    optionPath={!!anchorElement ? mdiCalendarRemoveOutline : mdiCalendarOutline}
                    onOption={showDayPicker}
                    onChange={handleInputChange}
                />
                <Popover
                    anchorEl={anchorElement}
                    open={!!anchorElement}
                    onClose={hideDayPicker}
                    disableRestoreFocus
                    disableEnforceFocus
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    style={{ zIndex: 9999 }}>
                    <S.DatePickerWrapper>{renderDayPicker()}</S.DatePickerWrapper>
                </Popover>
            </S.DatePicker>
        );
    },
);
